import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ChangePasswordFormProps {
  onSubmit: (data: { newPassword: string; confirmPassword: string }) => void;
  loading?: boolean;
}

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  onSubmit,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    new: false,
    confirm: false,
  });
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const togglePasswordVisibility = (field: 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.newPassword !== formData.confirmPassword) {
      setError(t('profile.passwordMismatch', 'Mật khẩu xác nhận không khớp'));
      return;
    }

    if (formData.newPassword.length < 6) {
      setError(t('profile.passwordTooShort', 'Mật khẩu phải có ít nhất 6 ký tự'));
      return;
    }

    onSubmit(formData);
  };

  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  useEffect(() => {
    const pwd = formData.newPassword;
    if (!pwd) {
      setNewPasswordError(null);
      return;
    }

    if (pwd.length < 6) {
      setNewPasswordError(t('profile.passwordTooShort', 'Mật khẩu phải có ít nhất 6 ký tự'));
    } else if (pwd.length > 20) {
      setNewPasswordError(t('profile.passwordTooLong', 'Mật khẩu không được vượt quá 20 ký tự'));
    } else if (!/[A-Z]/.test(pwd)) {
      setNewPasswordError(t('profile.passwordUppercase', 'Mật khẩu phải có ít nhất 1 chữ hoa'));
    } else if (!/[a-z]/.test(pwd)) {
      setNewPasswordError(t('profile.passwordLowercase', 'Mật khẩu phải có ít nhất 1 chữ thường'));
    } else if (!/[0-9]/.test(pwd)) {
      setNewPasswordError(t('profile.passwordNumber', 'Mật khẩu phải có ít nhất 1 chữ số'));
    } else {
      setNewPasswordError(null);
    }
  }, [formData.newPassword, t]);

  const [passwordConfirmError, setPasswordConfirmError] = useState<string | null>(null);
  useEffect(() => {
     if (formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword) {
      setPasswordConfirmError(t('profile.passwordMismatch', 'Mật khẩu xác nhận không khớp'));
    } else {
      setPasswordConfirmError(null);
      setNewPasswordError(null);
    }
  }, [formData.newPassword, formData.confirmPassword]);


  const EyeIcon = ({ show }: { show: boolean }) => (
    <svg
      className="w-5 h-5 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      {show ? (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </>
      ) : (
        <>
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </>
      )}
    </svg>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-6 w-full">
      {/* New Password */}
      <div>
        <label className="block text-sm font-medium !text-gray-700 dark:!text-gray-300 mb-1">
          {t('profile.newPassword', 'Mật khẩu mới')} <span className="!text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showPasswords.new ? 'text' : 'password'}
            name="newPassword"
            value={formData.newPassword}
            onChange={handleChange}
            placeholder={t('profile.enterNewPassword', 'Nhập mật khẩu mới')}
            className="w-full px-4 py-2.5 pr-10 border !border-gray-400 dark:!border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white !placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            required
          />
          <div className="absolute top-full left-0 mt-1">
            {newPasswordError && (
              <p className="text-sm text-red-600 dark:text-red-400">{newPasswordError}</p>
            )}
          </div>
          <button
            title='Toggle Password Visibility'
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
          >
            <EyeIcon show={showPasswords.new} />
          </button>
        </div>
      </div>

      {/* Confirm Password */}
      <div>
        <label className="block text-sm font-medium !text-gray-700 dark:!text-gray-300 mb-1">
          {t('profile.confirmPassword', 'Xác nhận mật khẩu')} <span className="!text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            type={showPasswords.confirm ? 'text' : 'password'}
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            placeholder={t('profile.enterConfirmPassword', 'Nhập lại mật khẩu mới')}
            className="w-full px-4 py-2.5 pr-10 border !border-gray-400 dark:!border-gray-600 rounded-md !bg-white dark:!bg-gray-700 !text-gray-900 dark:!text-white !placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            required
          />
          <div className="absolute top-full left-0 mt-1">
            {passwordConfirmError && (
              <p className="text-sm text-red-600 dark:text-red-400">{passwordConfirmError}</p>
            )}
          </div>
          <button
            title='Toggle Password Visibility'
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute right-3 top-1/2 -translate-y-1/2 hover:opacity-70"
          >
            <EyeIcon show={showPasswords.confirm} />
          </button>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4">
        {(
          <button
            type="button"
            onClick={() => {
              setFormData({ newPassword: '', confirmPassword: '' })

            }}
            className="px-6 py-2.5 border !border-red-500 !text-red-500 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 font-medium transition-colors"
          >
            {t('common.cancel', 'Hủy')}
          </button>
        )}
        <button
          type="submit"
          disabled={loading || !!newPasswordError || !!passwordConfirmError}
          className="px-8 py-2.5 bg-primary hover:!bg-primary/90 !text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? t('common.updating', 'Đang cập nhật...') : t('profile.updatePassword', 'Cập nhật mật khẩu')}
        </button>
      </div>
    </form>
  );
};

export default ChangePasswordForm;
